import React from 'react';
import { formatDate } from './utils/dateFormatter';
import {
	Button,
	Input,
	Textarea,
	Select,
	SelectItem,
	Chip,
	Divider,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter
} from '@heroui/react';
import { Edit, Trash2, MessageSquare, Save } from 'lucide-react';
import { STATUS_OPTIONS, PRIORITY_OPTIONS } from './DiscrepanciesPage.js';

export function DiscrepancyDetailModal({
	isOpen,
	onClose,
	selectedDiscrepancy,
	accessGroups,
	newComment,
	setNewComment,
	handleChangeStatus,
	handleAddComment,
	handleDelete,
	handleEdit,
	getPriorityInfo,
	getStatusInfo,
	getMechanicName
}) {
	if (!selectedDiscrepancy) return null;
	const priority = getPriorityInfo(selectedDiscrepancy.priority);
	const status = getStatusInfo(selectedDiscrepancy.status);
	const activities = selectedDiscrepancy.activities || [];

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="2xl"
			scrollBehavior="inside"
		>
			<ModalContent>
				{onClose => (
					<>
						<ModalHeader className="flex items-center gap-2">
							<span className="truncate">{selectedDiscrepancy.name}</span>
						</ModalHeader>
						<ModalBody>
							<div className="space-y-4">
								{/* Status and Priority */}
								<div className="flex gap-2 items-center flex-wrap">
									<Chip size="sm" color={priority.color} variant="flat">
										{priority.label} Priority
									</Chip>
									{(accessGroups._loaded && accessGroups.discrepanciesStatus) ? (
										<Select
											aria-label="Status"
											size="sm"
											selectedKeys={[selectedDiscrepancy.status]}
											onSelectionChange={keys => {
												const newStatus = Array.from(keys)[0];
												if (newStatus && newStatus !== selectedDiscrepancy.status) {
													handleChangeStatus(selectedDiscrepancy, newStatus);
												}
											}}
											className="w-40"
										>
											{STATUS_OPTIONS.map(opt => (
												<SelectItem key={opt.value}>{opt.label}</SelectItem>
											))}
										</Select>
									) : (
										<Chip size="sm" variant="flat">
											{STATUS_OPTIONS.find(o => o.value === selectedDiscrepancy.status)?.label || selectedDiscrepancy.status}
										</Chip>
									)}
								</div>

								{/* Description */}
								{selectedDiscrepancy.description && (
									<div>
										<p className="text-sm font-medium text-default-500 mb-1">Description</p>
										<p className="text-sm">{selectedDiscrepancy.description}</p>
									</div>
								)}

								{/* Details Grid */}
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<p className="text-default-500">Reported By</p>
										<p>
											{selectedDiscrepancy.authorName
												? `${selectedDiscrepancy.authorName} ${selectedDiscrepancy.authorSurname || ''}`.trim()
												: selectedDiscrepancy.author?.name
													? `${selectedDiscrepancy.author.name} ${selectedDiscrepancy.author.surname || ''}`.trim()
													: selectedDiscrepancy.signature || '-'}
										</p>
									</div>
									<div>
										<p className="text-default-500">Date</p>
										<p>{formatDate(selectedDiscrepancy.date)}</p>
									</div>
									<div>
										<p className="text-default-500">Assigned To</p>
										<p>{getMechanicName(selectedDiscrepancy.assignTo) || 'Not assigned'}</p>
									</div>
									{selectedDiscrepancy.logbookEntry && (
										<div>
											<p className="text-default-500">Logbook Entry</p>
											<p>{selectedDiscrepancy.logbookEntry}</p>
										</div>
									)}
								</div>

								<Divider />

								{/* Activity/Comments */}
								<div>
									<p className="text-sm font-medium text-default-500 mb-2">Activity</p>
									{activities.length > 0 ? (
										<div className="space-y-2 max-h-40 overflow-y-auto">
											{activities
												.flat()
												.filter(Boolean)
												.map((activity, idx) => (
													<div key={idx} className="p-2 rounded bg-default-50 text-sm">
														<p>{activity.disposition || activity.comment || '-'}</p>
														<p className="text-xs text-default-400 mt-1">
															{activity.authorName
																? `${activity.authorName} ${activity.authorSurname || ''}`.trim()
																: activity.author?.name
																	? `${activity.author.name} ${activity.author.surname || ''}`.trim()
																	: activity.signature || 'Unknown'}{' '}
															- {formatDate(activity.date)}
														</p>
													</div>
												))}
										</div>
									) : (
										<p className="text-sm text-default-400">No activity yet</p>
									)}
								</div>

								{/* Add Comment (discrepanciesComment permission) */}
								{(accessGroups._loaded && accessGroups.discrepanciesComment) && (
									<div className="flex gap-2">
										<Input
											size="sm"
											placeholder="Add a comment..."
											value={newComment}
											onChange={e => setNewComment(e.target.value)}
											className="flex-1"
										/>
										<Button
											size="sm"
											color="primary"
											isDisabled={!newComment.trim()}
											onPress={handleAddComment}
										>
											<MessageSquare className="w-4 h-4" />
										</Button>
									</div>
								)}
							</div>
						</ModalBody>
						{(accessGroups._loaded && (accessGroups.discrepanciesManage || accessGroups.discrepanciesDelete)) && (
							<ModalFooter>
								{accessGroups.discrepanciesDelete && (
									<Button
										color="danger"
										variant="flat"
										startContent={<Trash2 className="w-4 h-4" />}
										onPress={() => handleDelete(selectedDiscrepancy)}
									>
										Delete
									</Button>
								)}
								{accessGroups.discrepanciesManage && (
									<Button
										color="primary"
										startContent={<Edit className="w-4 h-4" />}
										onPress={() => {
											onClose();
											handleEdit(selectedDiscrepancy);
										}}
									>
										Edit
									</Button>
								)}
							</ModalFooter>
						)}
					</>
				)}
			</ModalContent>
		</Modal>
	);
}

export function DiscrepancyFormModal({
	isOpen,
	onClose,
	formMode,
	formData,
	setFormData,
	saving,
	mechanics,
	handleSave
}) {
	return (
		<Modal scrollBehavior="inside" isOpen={isOpen} onClose={onClose} size="lg">
			<ModalContent>
				{onClose => (
					<>
						<ModalHeader>{formMode === 'add' ? 'Add Discrepancy' : 'Edit Discrepancy'}</ModalHeader>
						<ModalBody>
							<div className="space-y-4">
								<Input
									label="Name"
									placeholder="Enter discrepancy name"
									value={formData.name}
									onChange={e => setFormData({ ...formData, name: e.target.value })}
									isRequired
								/>
								<Textarea
									label="Description"
									placeholder="Describe the discrepancy"
									value={formData.description}
									onChange={e => setFormData({ ...formData, description: e.target.value })}
									minRows={3}
									isRequired
								/>
								<div className="grid grid-cols-2 gap-4">
									<Select
										label="Priority"
										selectedKeys={[formData.priority]}
										onSelectionChange={keys =>
											setFormData({ ...formData, priority: Array.from(keys)[0] })
										}
										isRequired
									>
										{PRIORITY_OPTIONS.map(opt => (
											<SelectItem key={opt.value}>{opt.label}</SelectItem>
										))}
									</Select>
									<Select
										label="Assigned To"
										placeholder="Select mechanic"
										selectedKeys={formData.assignTo ? [formData.assignTo] : []}
										onSelectionChange={keys =>
											setFormData({ ...formData, assignTo: Array.from(keys)[0] || '' })
										}
									>
										{mechanics.map(m => (
											<SelectItem key={String(m.id)}>
												{`${m.name} ${m.surname || ''}`.trim()}
											</SelectItem>
										))}
									</Select>
								</div>
								<Input
									label="Logbook Entry"
									placeholder="Enter logbook entry reference"
									value={formData.logbookEntry}
									onChange={e => setFormData({ ...formData, logbookEntry: e.target.value })}
								/>
							</div>
						</ModalBody>
						<ModalFooter>
							<Button variant="light" onPress={onClose} isDisabled={saving}>
								Cancel
							</Button>
							<Button
								color="primary"
								startContent={<Save className="w-4 h-4" />}
								onPress={handleSave}
								isLoading={saving}
							>
								Save
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}
